import { memo, useCallback, useState } from 'react';
import { Button, Field, Modal } from '@/components/generic';
import { useUpdateCardAlias } from '@xhype/card-app-sdk';
import { useAuthStore } from '@package/smart-wallet/store';

type UpdateCardAliasProps = {
  cardId: number;
  currentAlias: string;
  onSuccess: () => void;
};

const UpdateCardAlias = ({
  cardId,
  currentAlias,
  onSuccess,
}: UpdateCardAliasProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alias, setAlias] = useState(currentAlias);
  const [error, setError] = useState<string>('');
  const userId = useAuthStore((store) => store.id);

  const updateCardAlias = useUpdateCardAlias({
    onSuccess: () => {
      setIsModalOpen(false);
      setError('');
      onSuccess();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.msg || 'Failed to update card alias');
    },
  });

  const handleOpenModal = useCallback(() => {
    setAlias(currentAlias);
    setError('');
    setIsModalOpen(true);
  }, [currentAlias]);

  const handleCloseModal = useCallback(() => {
    if (!updateCardAlias.isPending) {
      setIsModalOpen(false);
      setAlias(currentAlias);
      setError('');
    }
  }, [updateCardAlias.isPending, currentAlias]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!alias.trim()) {
        setError('Alias cannot be empty');
        return;
      }

      if (alias.trim() === currentAlias) {
        setError('Please enter a different alias');
        return;
      }

      if (alias.trim().length > 60) {
        setError('Alias cannot exceed 60 characters');
        return;
      }

      updateCardAlias.mutate({
        userId: userId ?? 0,
        cardId,
        alias: alias.trim(),
      });
    },
    [alias, currentAlias, updateCardAlias, userId, cardId],
  );

  return (
    <>
      <Button
        className='!px-4 !py-2 !h-8'
        isGray
        type='button'
        onClick={handleOpenModal}
      >
        Edit Alias
      </Button>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <div className='w-full max-w-md rounded-3xl shadow-2xl max-sm:p-6'>
          <div className='mb-6'>
            <h2 className='text-title-3 text-t-primary font-semibold mb-2'>
              Update Card Alias
            </h2>
            <p className='text-body-2 text-t-secondary'>
              Give your card a memorable name to easily identify it.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <Field
              label='Card Alias'
              type='text'
              placeholder='Enter card alias'
              value={alias}
              onChange={(e) => {
                setAlias(e.target.value);
                setError('');
              }}
              disabled={updateCardAlias.isPending}
              maxLength={60}
            />

            {error && (
              <div className='mt-2 text-error text-body-2'>{error}</div>
            )}

            <div className='flex gap-3 mt-6'>
              <Button
                className='flex-1'
                isGray
                type='button'
                onClick={handleCloseModal}
                disabled={updateCardAlias.isPending}
              >
                Cancel
              </Button>
              <Button
                className='flex-1'
                isBlack
                type='submit'
                disabled={
                  updateCardAlias.isPending ||
                  !alias.trim() ||
                  alias.trim() === currentAlias
                }
              >
                {updateCardAlias.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default memo(UpdateCardAlias);
